import {
  Box,
  Button,
  Dialog,
  Flex,
  Grid,
  HStack,
  Input,
  Portal,
  Text,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Globe, Lock, Save } from 'lucide-react'
import { useRef, useState } from 'react'
import { ConnectionPanel } from '@/components/mqtt-client/ConnectionPanel'
import { ConnectionStatus } from '@/components/mqtt-client/ConnectionStatus'
import { useMqttProxy } from '@/components/mqtt-client/hooks/useMqttProxy'
import { MessagesPanel } from '@/components/mqtt-client/MessagesPanel'
import { PublishPanel } from '@/components/mqtt-client/PublishPanel'
import { SubscriptionsPanel } from '@/components/mqtt-client/SubscriptionsPanel'
import { PageHeader } from '@/components/shell'
import { useAuth } from '@/contexts/AuthContext'
import { mqttProfilesApi } from '@/lib/api'
import type { ConnectionFormState, MqttProxyConnectionConfig, MqttQoS } from '@/lib/mqtt-types'
import { toaster } from '~/toaster'

export const Route = createFileRoute('/_app/mqtt-client')({
  component: MqttClientPage,
})

function MqttClientPage() {
  const queryClient = useQueryClient()
  const { hasMinRole } = useAuth()
  const isAdmin = hasMinRole('admin')
  const isMobile = useBreakpointValue({ base: true, lg: false })

  // Save profile modal state
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [profileName, setProfileName] = useState('')
  const [pendingConfig, setPendingConfig] = useState<ConnectionFormState | null>(null)
  const profileNameInputRef = useRef<HTMLInputElement>(null)

  const {
    status,
    clientId,
    messages,
    subscriptions,
    error,
    isPaused,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    removeSubscription,
    publish,
    clearMessages,
    togglePause,
    setMaxMessages,
  } = useMqttProxy({
    onConnect: (clientId) => {
      toaster.success({
        title: 'Connected',
        description: `Connected as ${clientId}`,
        duration: 4000,
      })
    },
    onDisconnect: (reason) => {
      if (reason) {
        toaster.info({
          title: 'Disconnected',
          description: reason,
          duration: 5000,
        })
      }
    },
    onError: (error, details) => {
      toaster.error({
        title: 'Connection Error',
        description: details ? `${error}\n${details}` : error,
        duration: 8000,
      })
    },
    onSubscribed: (topic, qos) => {
      toaster.success({
        title: 'Subscribed',
        description: `Subscribed to "${topic}" with QoS ${qos}`,
        duration: 3000,
      })
    },
    onUnsubscribed: (topic) => {
      toaster.info({
        title: 'Unsubscribed',
        description: `Unsubscribed from "${topic}"`,
        duration: 3000,
      })
    },
    onSubscribeError: (topic, error) => {
      toaster.error({
        title: 'Subscription Failed',
        description: `Failed to subscribe to "${topic}": ${error}`,
        duration: 6000,
      })
    },
    onPublishError: (topic, error) => {
      toaster.error({
        title: 'Publish Failed',
        description: `Failed to publish to "${topic}": ${error}`,
        duration: 6000,
      })
    },
  })

  // Save profile mutation (private)
  const saveProfileMutation = useMutation({
    mutationFn: (data: { config: ConnectionFormState; name: string }) =>
      mqttProfilesApi.create({
        name: data.name,
        protocol: data.config.protocol,
        host: data.config.host,
        port: data.config.port,
        path: data.config.path || undefined,
        username: data.config.username || undefined,
        password: data.config.password || undefined,
        clientIdPrefix: data.config.clientIdPrefix || undefined,
        tlsInsecure: data.config.tlsInsecure,
        mqttVersion: data.config.mqttVersion,
        cleanSession: data.config.cleanSession,
        keepAlive: data.config.keepAlive,
        maxMessages: data.config.maxMessages,
        lwTopic: data.config.lwTopic || undefined,
        lwMessage: data.config.lwMessage || undefined,
        lwQos: data.config.lwQos,
        lwRetain: data.config.lwRetain,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mqtt-profiles'] })
      toaster.success({
        title: 'Profile Saved',
        description: 'Connection profile saved successfully',
      })
    },
    onError: (error: Error) => {
      toaster.error({
        title: 'Save Failed',
        description: error.message,
      })
    },
  })

  // Save public profile mutation (admin only)
  const savePublicProfileMutation = useMutation({
    mutationFn: (data: { config: ConnectionFormState; name: string }) =>
      mqttProfilesApi.createPublic({
        name: data.name,
        protocol: data.config.protocol,
        host: data.config.host,
        port: data.config.port,
        path: data.config.path || undefined,
        username: data.config.username || undefined,
        password: data.config.password || undefined,
        clientIdPrefix: data.config.clientIdPrefix || undefined,
        tlsInsecure: data.config.tlsInsecure,
        mqttVersion: data.config.mqttVersion,
        cleanSession: data.config.cleanSession,
        keepAlive: data.config.keepAlive,
        maxMessages: data.config.maxMessages,
        lwTopic: data.config.lwTopic || undefined,
        lwMessage: data.config.lwMessage || undefined,
        lwQos: data.config.lwQos,
        lwRetain: data.config.lwRetain,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mqtt-profiles'] })
      closeSaveModal()
      toaster.success({
        title: 'Public Profile Saved',
        description: 'Public connection profile saved successfully. All users can now access it.',
      })
    },
    onError: (error: Error) => {
      toaster.error({
        title: 'Save Failed',
        description: error.message,
      })
    },
  })

  const openSaveModal = (config: ConnectionFormState) => {
    setPendingConfig(config)
    setProfileName('')
    setIsSaveModalOpen(true)
  }

  const closeSaveModal = () => {
    setIsSaveModalOpen(false)
    setPendingConfig(null)
    setProfileName('')
  }

  const handleSaveAsPrivate = () => {
    if (!pendingConfig || !profileName.trim()) {
      return
    }
    saveProfileMutation.mutate({ config: pendingConfig, name: profileName.trim() })
    closeSaveModal()
  }

  const handleSaveAsPublic = () => {
    if (!pendingConfig || !profileName.trim()) {
      return
    }
    savePublicProfileMutation.mutate({ config: pendingConfig, name: profileName.trim() })
  }

  const handleConnect = async (config: ConnectionFormState, profileId?: string) => {
    let password = config.password || undefined

    // If connecting with a saved profile and no password was manually entered,
    // fetch the saved password from the backend
    if (profileId && !config.password) {
      try {
        const result = await mqttProfilesApi.getPassword(profileId)
        password = result.password || undefined
      } catch (error) {
        console.error('Failed to fetch profile password:', error)
        // Continue without password - the broker may not require auth
      }
    }

    // Update max messages setting before connecting
    setMaxMessages(config.maxMessages || 1000)

    const proxyConfig: MqttProxyConnectionConfig = {
      protocol: config.protocol,
      host: config.host,
      port: config.port,
      path: config.path || undefined,
      username: config.username || undefined,
      password: password,
      clientIdPrefix: config.clientIdPrefix || undefined,
      tlsInsecure: config.tlsInsecure,
      mqttVersion: config.mqttVersion,
      cleanSession: config.cleanSession,
      keepAlive: config.keepAlive,
      lwTopic: config.lwTopic || undefined,
      lwMessage: config.lwMessage || undefined,
      lwQos: config.lwQos,
      lwRetain: config.lwRetain,
    }
    connect(proxyConfig)
  }

  const handleSaveProfile = (config: ConnectionFormState) => {
    openSaveModal(config)
  }

  const handlePublish = (
    topic: string,
    payload: string,
    qos: MqttQoS,
    retain: boolean,
    userProperties?: Record<string, string>,
  ) => {
    if (status !== 'connected') {
      toaster.error({
        title: 'Not Connected',
        description: 'Please connect to a broker before publishing',
        duration: 4000,
      })
      return
    }
    publish(topic, payload, qos, retain, userProperties)
    // Success toast will be triggered by onPublished callback from backend
    toaster.success({
      title: 'Publishing...',
      description: `Sending message to ${topic}`,
      duration: 2000,
    })
  }

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="MQTT Client"
        subtitle="Connect to any MQTT broker over WebSocket and inspect live traffic."
        actions={<ConnectionStatus status={status} clientId={clientId} />}
      />

      {/* Error display */}
      {error && (
        <Box
          mb={4}
          p={3}
          bg="danger.subtle"
          borderRadius="md"
          borderLeftWidth={4}
          borderLeftColor="danger.emphasized"
        >
          <Text fontSize="sm" color="danger.fg">
            {error}
          </Text>
        </Box>
      )}

      {/* Main layout */}
      {isMobile ? (
        // Mobile: Stack vertically
        <Flex direction="column" gap={4}>
          <ConnectionPanel
            status={status}
            onConnect={handleConnect}
            onDisconnect={disconnect}
            onSaveProfile={handleSaveProfile}
          />
          <SubscriptionsPanel
            subscriptions={subscriptions}
            status={status}
            onSubscribe={subscribe}
            onUnsubscribe={unsubscribe}
            onRemove={removeSubscription}
            defaultOpen={status !== 'connected'}
          />
          <PublishPanel
            status={status}
            onPublish={handlePublish}
            defaultOpen={status === 'connected'}
          />
          <Box h="400px">
            <MessagesPanel
              messages={messages}
              subscriptions={subscriptions}
              isPaused={isPaused}
              onClear={clearMessages}
              onTogglePause={togglePause}
            />
          </Box>
        </Flex>
      ) : (
        // Desktop: 2-column grid
        <Grid
          templateColumns="350px 1fr"
          templateRows="auto 1fr"
          gap={4}
          minH="calc(100vh - 200px)"
        >
          {/* Left column: Connection */}
          <Box gridRow="1 / 3" gridColumn="1">
            <ConnectionPanel
              status={status}
              onConnect={handleConnect}
              onDisconnect={disconnect}
              onSaveProfile={handleSaveProfile}
            />
          </Box>

          {/* Right top: Subscriptions + Publish stacked */}
          <Flex direction="column" gap={4} gridRow="1" gridColumn="2">
            <SubscriptionsPanel
              subscriptions={subscriptions}
              status={status}
              onSubscribe={subscribe}
              onUnsubscribe={unsubscribe}
              onRemove={removeSubscription}
              defaultOpen={status !== 'connected'}
            />
            <PublishPanel
              status={status}
              onPublish={handlePublish}
              defaultOpen={status === 'connected'}
            />
          </Flex>

          {/* Right bottom: Messages */}
          <Box gridRow="2" gridColumn="2" minH="400px">
            <MessagesPanel
              messages={messages}
              subscriptions={subscriptions}
              isPaused={isPaused}
              onClear={clearMessages}
              onTogglePause={togglePause}
            />
          </Box>
        </Grid>
      )}

      {/* Save Profile Modal */}
      <Dialog.Root
        open={isSaveModalOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            closeSaveModal()
          }
        }}
        initialFocusEl={() => profileNameInputRef.current}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="md">
              <Dialog.Header>
                <Dialog.Title>Save Connection Profile</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <VStack gap={4} align="stretch">
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      Profile Name
                    </Text>
                    <Input
                      ref={profileNameInputRef}
                      placeholder="Enter a name for this profile"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && profileName.trim()) {
                          if (isAdmin) {
                            // Default to private for Enter key
                            handleSaveAsPrivate()
                          } else {
                            handleSaveAsPrivate()
                          }
                        }
                      }}
                    />
                  </Box>

                  {pendingConfig && (
                    <Box p={3} bg="bg.subtle" borderRadius="md" fontSize="sm">
                      <Text color="content.secondary">
                        {pendingConfig.protocol.toUpperCase()}://{pendingConfig.host}:
                        {pendingConfig.port}
                        {pendingConfig.username && ' (authenticated)'}
                      </Text>
                    </Box>
                  )}

                  {isAdmin && (
                    <Box
                      p={3}
                      bg="info.subtle"
                      borderRadius="md"
                      borderLeftWidth={3}
                      borderLeftColor="info.emphasized"
                    >
                      <Text fontSize="sm" color="info.fg">
                        As an admin, you can save this profile as public (visible to all users) or
                        private (only for you).
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack gap={3} justify="flex-end" w="100%">
                  <Button variant="ghost" onClick={closeSaveModal}>
                    Cancel
                  </Button>
                  {isAdmin ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleSaveAsPrivate}
                        disabled={!profileName.trim()}
                        loading={saveProfileMutation.isPending}
                      >
                        <Lock size={16} />
                        Save as Private
                      </Button>
                      <Button
                        colorPalette="brand"
                        onClick={handleSaveAsPublic}
                        disabled={!profileName.trim()}
                        loading={savePublicProfileMutation.isPending}
                      >
                        <Globe size={16} />
                        Save as Public
                      </Button>
                    </>
                  ) : (
                    <Button
                      colorPalette="brand"
                      onClick={handleSaveAsPrivate}
                      disabled={!profileName.trim()}
                      loading={saveProfileMutation.isPending}
                    >
                      <Save size={16} />
                      Save Profile
                    </Button>
                  )}
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  )
}

export default MqttClientPage
